/**
 * @module util/portPicker/searchExecutor
 * Fan a picked lane out across nearest-port pairs, run the rate queries in parallel,
 * then merge + tag + dedupe the cards (PRD §6.3 / UX §5). The actual per-lane rate call
 * is injected (`searchLane`) so this stays pure and unit-testable without the API.
 */

/** Total "closeness" of a pair — smaller is nearer (nulls treated as far). */
const pairDistance = (pair) =>
  (pair?.origin?.distanceKm ?? Infinity) + (pair?.destination?.distanceKm ?? Infinity);

/**
 * Run `searchLane(originPort, destPort, pair)` for every pair concurrently; keep only
 * pairs that returned a non-empty rate array. Never rejects — a failed lane is dropped.
 * @returns {Promise<Array<{pair,rates:Array}>>}
 */
export const runParallelLaneSearch = async (pairs, searchLane) => {
  if (!Array.isArray(pairs) || pairs.length === 0) return [];
  // async wrapper so a SYNCHRONOUS throw in searchLane becomes a rejected promise that
  // allSettled catches (rather than escaping the map and rejecting the whole call).
  const settled = await Promise.allSettled(
    pairs.map(async (p) => searchLane(p.origin.port, p.destination.port, p))
  );
  const out = [];
  settled.forEach((s, i) => {
    const rates = s.status === 'fulfilled' ? s.value : null;
    if (Array.isArray(rates) && rates.length) out.push({ pair: pairs[i], rates });
  });
  return out;
};

/** Attach `_source` (origin/dest port + distance) to each rate card. */
export const tagLaneResults = (laneResults) =>
  (laneResults || []).flatMap(({ pair, rates }) =>
    rates.map((rate) => ({
      ...rate,
      _source: {
        originPort: pair.origin.port.Name,
        originPortId: pair.origin.port.id,
        originKm: pair.origin.distanceKm ?? null,
        destPort: pair.destination.port.Name,
        destPortId: pair.destination.port.id,
        destKm: pair.destination.distanceKm ?? null,
      },
    }))
  );

/**
 * Dedupe identity for a rate card: a stable string of everything EXCEPT our injected tags
 * (`_source`/`_dist`) and the mutable `blockRoute` flag. Two cards with the same key are
 * byte-identical real duplicates (same carrier, price, transit, schedule, …) that differ
 * only in which source port produced them — safe to collapse. Anything that differs yields
 * a different key, so distinct options are always kept. Reliable here because every card in
 * one search comes from the same rate API → consistent key ordering.
 */
export const structuralRateKey = (rate) => {
  const { _source, _dist, blockRoute, ...rest } = rate || {};
  return JSON.stringify(rest);
};

/**
 * Collapse cards that are identical per `keyOf` (e.g. `structuralRateKey`), keeping the one
 * from the nearest source ports. Result is sorted nearest-source first.
 */
export const dedupeRates = (taggedRates, keyOf) => {
  const best = new Map();
  for (const rate of taggedRates || []) {
    const key = keyOf(rate);
    const dist = (rate._source?.originKm ?? Infinity) + (rate._source?.destKm ?? Infinity);
    const current = best.get(key);
    if (!current || dist < current._dist) best.set(key, { ...rate, _dist: dist });
  }
  return [...best.values()]
    .sort((a, b) => a._dist - b._dist)
    .map(({ _dist, ...rate }) => rate);
};

/**
 * End-to-end: pairs → parallel search → tag → dedupe. `keyOf` identifies a duplicate card.
 * @returns {Promise<Array>} merged, tagged, deduped, nearest-first rate cards.
 */
export const executeLaneSearch = async (pairs, searchLane, keyOf) => {
  const laneResults = await runParallelLaneSearch(pairs, searchLane);
  return dedupeRates(tagLaneResults(laneResults), keyOf);
};

export { pairDistance };
